import { createColumnHelper } from "@tanstack/react-table";

import type {
  DownloadBanner,
  GetLanguageLabel,
  MarketingBannerRow,
  MarketingBannersTranslations,
  MarketingBannerType,
} from "./types";

import { isNoCampaignName } from "../Campaign/isNoCampaignName";
import {
  formatRebateProgram,
  LastClickHeaderCell,
  ReferralLastClickDataCell,
} from "../ReferralLinkModals";
import { dayjs, formatDateWithUtcOffset } from "../utils/date";
import { BannerNameCell } from "./BannerNameCell";
import { BannerDataCell, BannerHeaderCell, BannerTooltipText } from "./Cells";
import { DetailsCell } from "./DetailsCell";

const columnHelper = createColumnHelper<MarketingBannerRow>();

const TYPE_LABEL: Record<MarketingBannerType, string> = {
  html_ad: "HTML",
  image_banner: "Image",
};

export const marketingBannerColumns = (
  translations: MarketingBannersTranslations,
  options: {
    getLanguageLabel: GetLanguageLabel;
    onRename?: (record: MarketingBannerRow) => void;
    onDownload?: DownloadBanner;
    showLastClicked?: boolean;
    showCampaign?: boolean;
    getPerformanceHref?: (banner: MarketingBannerRow) => string | undefined;
    onViewPerformance?: (banner: MarketingBannerRow) => void;
    onNavigateToPerformance?: (href: string) => void;
    onCopy?: (record: MarketingBannerRow) => void;
    onView?: (record: MarketingBannerRow) => void;
    onMoreMenuCopy?: (record: MarketingBannerRow) => void;
    onMenuOpen?: (record: MarketingBannerRow) => void;
    /** Fired before the row's preview modal "Copy HTML code" action writes to the clipboard. */
    onPreviewCopy?: (record: MarketingBannerRow) => void;
  },
) => {
  const campaignColumn = columnHelper.accessor("campaign", {
    id: "campaign",
    header: () => <BannerHeaderCell text={translations.columns.campaign} />,
    cell: (info) => {
      const campaign = info.getValue();
      return campaign && !isNoCampaignName(campaign.name) ? (
        <BannerDataCell
          text={campaign.name}
          className="[overflow-wrap:anywhere]"
        />
      ) : (
        <BannerDataCell
          text={translations.columns.noCampaign}
          textColor="text-text-disabled"
        />
      );
    },
    enableSorting: true,
    sortingFn: "alphanumeric",
    sortDescFirst: true,
    meta: {
      headerClassName: "w-[16.5%]",
      cellClassName: "w-[16.5%] !px-4 !py-[22px]",
    },
  });

  const lastClickedColumn = columnHelper.accessor("last_clicked_at", {
    id: "lastClickedAt",
    header: () => (
      <LastClickHeaderCell
        title={translations.columns.lastClick}
        tooltipText={translations.columns.lastClickTooltip}
      />
    ),
    cell: (info) => (
      <ReferralLastClickDataCell
        lastClickedAt={info.getValue()}
        translations={translations.lastClickCell}
      />
    ),
    enableSorting: true,
    sortingFn: "datetime",
    // Was `false` under PART-4056 (its own, deliberately different
    // convention); flipped to `true` under PART-4147 so every column in
    // this table — including this one — shares the same descending-first
    // toggle order.
    sortDescFirst: true,
    meta: {
      headerClassName: "w-[14%]",
      cellClassName: "w-[14%] !px-4 !py-[22px]",
    },
  });

  return [
    columnHelper.accessor("name", {
      header: () => <BannerHeaderCell text={translations.columns.bannerName} />,
      cell: (info) => (
        <BannerNameCell
          banner={info.row.original}
          translations={translations}
          getLanguageLabel={options.getLanguageLabel}
          onRename={
            options.onRename
              ? () => options.onRename?.(info.row.original)
              : undefined
          }
          onDownload={options.onDownload}
          performanceHref={options.getPerformanceHref?.(info.row.original)}
          onViewPerformance={
            options.onViewPerformance
              ? () => options.onViewPerformance?.(info.row.original)
              : undefined
          }
          onNavigateToPerformance={options.onNavigateToPerformance}
          onCopy={
            options.onCopy
              ? () => options.onCopy?.(info.row.original)
              : undefined
          }
          onView={
            options.onView
              ? () => options.onView?.(info.row.original)
              : undefined
          }
          onMoreMenuCopy={
            options.onMoreMenuCopy
              ? () => options.onMoreMenuCopy?.(info.row.original)
              : undefined
          }
          onMenuOpen={
            options.onMenuOpen
              ? () => options.onMenuOpen?.(info.row.original)
              : undefined
          }
          onPreviewCopy={
            options.onPreviewCopy
              ? () => options.onPreviewCopy?.(info.row.original)
              : undefined
          }
        />
      ),
      enableSorting: true,
      sortingFn: "alphanumeric",
      // Without this, TanStack infers first-click direction from the
      // column's raw value type (string ⇒ ascending), which put every
      // plain column here out of step with the rest of the portal's
      // sort-toggle convention (1st click descending, 2nd ascending) — PART-4147.
      sortDescFirst: true,
      meta: {
        headerClassName: "w-[27.5%] !px-3 !py-1",
        cellClassName: "w-[27.5%] !p-0 !py-0",
      },
    }),
    columnHelper.accessor("type", {
      header: () => <BannerHeaderCell text={translations.columns.type} />,
      cell: (info) => <BannerDataCell text={TYPE_LABEL[info.getValue()]} />,
      enableSorting: true,
      sortingFn: "alphanumeric",
      sortDescFirst: true,
      meta: {
        headerClassName: "w-[16.5%]",
        cellClassName: "w-[16.5%] !p-4",
      },
    }),
    columnHelper.accessor("banner_group", {
      // BE's sort whitelist expects camelCase (`bannerGroup`); without this the
      // column id defaults to the accessor key `banner_group` and the BE silently
      // falls back to `ORDER BY mb.created_at DESC`.
      id: "bannerGroup",
      header: () => <BannerHeaderCell text={translations.columns.details} />,
      cell: (info) => (
        <DetailsCell
          bannerGroup={info.getValue()}
          width={info.row.original.width}
          height={info.row.original.height}
          language={info.row.original.language}
          getLanguageLabel={options.getLanguageLabel}
        />
      ),
      enableSorting: true,
      sortingFn: "alphanumeric",
      sortDescFirst: true,
      meta: {
        headerClassName: "w-[23%]",
        cellClassName: "w-[23%] !p-0",
      },
    }),
    ...(options.showCampaign ? [campaignColumn] : []),
    columnHelper.accessor("hoid", {
      // BE sorts by `rebateProgram` (= Referral.Offers_ID); the row holds it
      // under `hoid` for legacy reasons. Override the column id so the FE→BE
      // sort key matches.
      id: "rebateProgram",
      header: () => (
        <BannerHeaderCell text={translations.columns.rebateProgram} />
      ),
      cell: (info) => (
        <BannerDataCell text={formatRebateProgram(info.getValue())} />
      ),
      enableSorting: true,
      sortingFn: "alphanumeric",
      sortDescFirst: true,
      meta: {
        headerClassName: "w-[16.5%]",
        cellClassName: "w-[16.5%] !px-4 !py-[22px]",
      },
    }),
    columnHelper.accessor("created_at", {
      id: "createdAt",
      header: () => <BannerHeaderCell text={translations.columns.created} />,
      cell: (info) => {
        const date = dayjs(info.getValue());
        if (!date.isValid()) {
          return <BannerDataCell text="" />;
        }
        return (
          <BannerTooltipText
            text={date.format("DD MMM YYYY")}
            tooltipText={formatDateWithUtcOffset(date)}
          />
        );
      },
      enableSorting: true,
      sortingFn: "datetime",
      sortDescFirst: true,
      meta: {
        headerClassName: "w-[16.5%]",
        cellClassName: "w-[16.5%] !px-4 !py-[22px]",
      },
    }),
    ...(options.showLastClicked ? [lastClickedColumn] : []),
  ];
};
